"use client";

import React, { useEffect, useState } from "react";
import useSWR from "swr";
import LoginPage from "./LoginPage";
import { Box, CircularProgress } from "@mui/material";
import { getApiUrl } from "@/utils/api";
import { fetcher } from "@/utils/fetch";
import { usePathname, redirect } from "next/navigation";

interface AuthWrapperProps {
    children: React.ReactNode;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
    const [isLoadingToken, setIsLoadingToken] = useState(true);
    const pathname = usePathname();

    useEffect(() => {
        setIsLoadingToken(false);
    }, []);

    // Check if current path should bypass authentication
    const isUnauthenticatedRoute =
        pathname === "/" ||
        pathname === "/forgot-password" ||
        pathname === "/reset-password" ||
        pathname === "/landing" ||
        pathname === "/enrollment" ||
        pathname === "/portal" ||
        pathname === "/survey" ||
        pathname === "/upload" ||
        pathname === "/industry-report" ||
        pathname === "/edit-participants" ||
        pathname === "/contact" ||
        pathname === "/privacy" ||
        pathname === "/faqs" ||
        pathname === "/sso";

    const {
        data: userData,
        error: authError,
        isLoading: isValidatingToken,
    } = useSWR(
        isUnauthenticatedRoute ? null : getApiUrl("/user_info"),
        // Opt out of auto re-auth: this check IS the re-auth path — on a 401 it
        // renders LoginPage below, so it must not trigger a reload loop.
        (fetchUrl: string) => {
            return fetcher(fetchUrl, "GET", {}, null, false, { redirectOnUnauthorized: false });
        },
        {
            shouldRetryOnError: false,
            // Don't refetch /user_info on window focus/reconnect: on the login screen (no token)
            // that flips isLoading -> shows the spinner -> unmounts LoginPage, wiping typed/pasted
            // credentials whenever focus returns (e.g. after copying from another window).
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    );

    // If it's an unauthenticated route, render children directly without LayoutContent
    if (isUnauthenticatedRoute) {
        return <>{children}</>;
    }

    if (isLoadingToken || isValidatingToken) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (authError || !userData) {
        return <LoginPage />;
    }

    // Force a password change before letting the user into the app (no email needed —
    // /reset-password falls back to the auth cookie token). /reset-password itself is a
    // bypass route above, so this won't loop.
    if (userData.force_password_change) {
        redirect("/reset-password");
    }

    return <>{children}</>;
};

export default AuthWrapper;
